import type { ClientToServer, ServerToClient, ClientDataKind } from "@lupp/shared";
import { getSocket } from "./socket";
import { BACKEND_URL } from "./backend-config";

/* global Word, Office */

/**
 * Liefert dem server-seitigen Loop auf Anfrage (`client_request`) Client-Daten:
 *  - **document**: der Client lädt das aktuelle docx per HTTP hoch (jobId = requestId);
 *    der Server parst das OOXML zum reichen DocumentModel (ADR/Phase-1). Kein WS-Transfer
 *    der (großen) Bytes.
 *  - **selection**: der beim Absenden gepufferte markierte Text (der Nutzer kann seine
 *    Selektion während der AI-Bearbeitung ändern — wir antworten mit dem Stand vom Absenden).
 */

let cachedSelection = "";

async function readSelectionText(): Promise<string> {
  try {
    return await Word.run(async (context) => {
      const sel = context.document.getSelection();
      sel.load("text");
      await context.sync();
      return sel.text ?? "";
    });
  } catch {
    return "";
  }
}

/** Snapshot the current selection at send time (focus may move/change during the turn). */
export async function captureSelectionForTurn(): Promise<void> {
  cachedSelection = await readSelectionText();
}

/** Reads the whole document as docx bytes via Office.js file slices. */
function getDocxBytes(): Promise<Uint8Array> {
  return new Promise<Uint8Array>((resolve, reject) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: 1024 * 1024 }, (res) => {
      if (res.status !== Office.AsyncResultStatus.Succeeded) return reject(res.error);
      const file = res.value;
      if (file.sliceCount === 0) {
        file.closeAsync();
        return resolve(new Uint8Array());
      }
      const slices: number[][] = new Array(file.sliceCount);
      let received = 0;
      let failed = false;
      for (let i = 0; i < file.sliceCount; i++) {
        file.getSliceAsync(i, (r) => {
          if (failed) return;
          if (r.status !== Office.AsyncResultStatus.Succeeded) {
            failed = true;
            file.closeAsync();
            return reject(r.error);
          }
          slices[r.value.index] = r.value.data as number[];
          if (++received === file.sliceCount) {
            file.closeAsync();
            const total = slices.reduce((n, s) => n + s.length, 0);
            const bytes = new Uint8Array(total);
            let off = 0;
            for (const s of slices) {
              bytes.set(s, off);
              off += s.length;
            }
            resolve(bytes);
          }
        });
      }
    });
  });
}

/** Document bytes + the paragraph uniqueLocalIds in document order (for server-side order-zip). */
async function getDocxWithParagraphIds(): Promise<{ bytes: Uint8Array; paragraphIds: string[]; title: string }> {
  const meta = await Word.run(async (context) => {
    context.document.properties.load("title");
    context.document.body.paragraphs.load("items/uniqueLocalId");
    await context.sync();
    const paragraphIds = context.document.body.paragraphs.items.map((p, i) => {
      const uid = (p as unknown as { uniqueLocalId?: string }).uniqueLocalId;
      return uid && uid.length > 0 ? uid : String(i);
    });
    return { title: context.document.properties.title || "", paragraphIds };
  });
  const bytes = await getDocxBytes();
  return { bytes, paragraphIds: meta.paragraphIds, title: meta.title };
}

/** Uploads the docx for the given job (HTTP, x-socket-id auth) so the server can parse it. */
async function uploadDocument(jobId: string, bytes: Uint8Array, paragraphIds: string[], title: string): Promise<void> {
  const form = new FormData();
  form.append(
    "file",
    new Blob([bytes as unknown as BlobPart], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }),
    "document.docx",
  );
  form.append("jobId", jobId);
  form.append("paragraphIds", JSON.stringify(paragraphIds));
  form.append("title", title);
  const res = await fetch(`${BACKEND_URL}/api/word/document`, {
    method: "POST",
    headers: { "x-socket-id": getSocket().id ?? "" },
    body: form,
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Document upload failed (${res.status}): ${msg}`);
  }
}

/** Listen for server round-trip requests and answer with the document (HTTP) / cached selection (WS). */
export function subscribeClientRequests(): () => void {
  const socket = getSocket();

  const respond = async (requestId: string, kind: ClientDataKind): Promise<void> => {
    if (kind === "document") {
      try {
        const { bytes, paragraphIds, title } = await getDocxWithParagraphIds();
        await uploadDocument(requestId, bytes, paragraphIds, title);
      } catch (err) {
        console.warn("Document upload failed:", err); // the server job times out → tool reports unavailable
      }
    } else {
      socket.emit("client_result", { type: "client_result", requestId, selectionText: cachedSelection } satisfies ClientToServer);
    }
  };

  const onEvent = (event: ServerToClient): void => {
    if (event.type === "client_request") void respond(event.requestId, event.kind);
  };

  socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
  return () => socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
}
